import {
  GridFeatureMode,
  GridFilterModel,
  GridInitialState,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { Dispatch, useCallback, useEffect, useMemo, useRef } from "react";
import Pagination from "src/Components/DataGrid/Pagination";

export function useGridDataQuery({
  initialState,
  setQueryParams,
  parseFilterModel,
  parsePaginationModel,
  parseSortModel,
}: {
  initialState?: GridInitialState;
  setQueryParams: Dispatch<
    React.SetStateAction<
      Record<string, string[] | string | number | undefined> | undefined
    >
  >;
  parseFilterModel: (
    model?: GridFilterModel
  ) => Record<string, string[] | string | undefined>;
  parsePaginationModel: (
    model?: Partial<GridPaginationModel>
  ) => Record<string, string | number>;
  parseSortModel: (
    model?: GridSortModel
  ) => Record<string, string[] | string | undefined>;
}) {
  const initialized = useRef(false);

  const setPaginationModel = useCallback(
    (model: GridPaginationModel) => {
      setQueryParams((payload) => ({
        ...(payload || {}),
        ...parsePaginationModel(model),
      }));
    },
    [parsePaginationModel, setQueryParams]
  );
  const setSortModel = useCallback(
    (model: GridSortModel) => {
      setQueryParams((payload) => ({
        ...(payload || {}),
        ...parseSortModel(model),
      }));
    },
    [parseSortModel, setQueryParams]
  );
  const setFilterModel = useCallback(
    (model: GridFilterModel) => {
      setQueryParams((payload) => ({
        ...(payload || {}),
        ...parseFilterModel(model),
      }));
    },
    [parseFilterModel, setQueryParams]
  );

  useEffect(() => {
    if (initialState && !initialized.current) {
      const { filter, pagination, sorting } = initialState;
      setQueryParams({
        ...parsePaginationModel(pagination?.paginationModel),
        ...parseSortModel(sorting?.sortModel),
        ...parseFilterModel(filter?.filterModel),
      });
      initialized.current = true;
    }
  }, [
    initialState,
    parseFilterModel,
    parsePaginationModel,
    parseSortModel,
    setQueryParams,
  ]);

  const dataGridProps = useMemo(
    () => ({
      initialState,
      pageSizeOptions: [5, 10, 25, 50, 100],
      pagination: true,
      paginationMode: "server" as GridFeatureMode,
      sortingMode: "server" as GridFeatureMode,
      filterMode: "server" as GridFeatureMode,
      onPaginationModelChange: setPaginationModel,
      onSortModelChange: setSortModel,
      onFilterModelChange: setFilterModel,
      slotProps: {
        basePagination: {
          material: {
            ActionsComponent: Pagination,
          },
        },
        toolbar: {
          csvOptions: { disableToolbarButton: true },
          printOptions: { disableToolbarButton: true },
          showQuickFilter: false,
        },
      },
    }),
    [initialState, setFilterModel, setPaginationModel, setSortModel]
  );

  return {
    dataGridProps,
  };
}
